import { AllMetrics } from '@pubstack/common/src/analytics/query'
import { NonNullable } from '@pubstack/common/src/assertion'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { Timezone } from '@pubstack/common/src/timezone'
import { DateTime } from 'luxon'
import { ReactElement, ReactNode, useMemo, useState } from 'react'
import { ChartWrapperOptions, GoogleChartWrapperChartType, ReactGoogleChartProps } from 'react-google-charts'
import { Colors } from '~/assets/style/colors'
import { ChartTooltipMetricProps, generateChartTooltip } from '~/components/ChartTooltip'
import { LegendProps } from '~/components/Legend'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { TimelineMetricProps } from '~/components/TimelineMetric'
import { asPercentage, Formula } from '~/modules/analytics/common/formulas/operation'
import { ANALYTICS_CHART_COLOR_CONFIG, getChartTooltipDateFormat, parseDateTime } from '~/utils/analytics'
import { AnalyticsDefaultChartOptions, convertChartDateTime } from './AnalyticsCharts'
import { AnalyticsChartWidgetMessage } from './AnalyticsChartWidget'

type DataPointConfig<T extends Partial<AllMetrics>> = Omit<LegendProps, 'className' | 'label' | 'value' | 'tooltip'> & {
  name: string | 'comparisonLine'
  getFormula: GetFormula<T>
  label?: string
  getTooltipLabel?: (args: TooltipArgs<T>) => string
  isComputable?: (args: BuildArgs<T>) => boolean
  legendTooltip?: ReactNode
  withValue?: boolean
}
type BuildArgs<T extends Partial<AllMetrics>> = {
  data: T
  comparisonData?: T
  currencySymbol: CurrencySymbol
}
type GetFormula<T extends Partial<AllMetrics>> = (args: BuildArgs<T>) => Formula<any> | undefined
export type TimelineConfiguration<T extends Partial<AllMetrics>> = {
  getChartOptions?: (args: BuildArgs<T> & { legendsConfig: string[] }) => Partial<ChartWrapperOptions['options']>
  legendConfig: string[]
  tooltipConfig: string[] | string[][]
  dataConfig: DataPointConfig<T>[]
}
export type MetricConfiguration<T extends Partial<AllMetrics>> = {
  metric: {
    name: string
    tooltipText?: string | ReactElement
    message?: (args: BuildArgs<T>) => AnalyticsChartWidgetMessage | undefined
  }
  dataConfig: DataPointConfig<T>[]
}

const isMultiLevel = <T,>(array: T[] | T[][]): array is T[][] => array && array.length > 0 && Array.isArray(array[0])
const isMultiLevelDataPointConfig = <T extends Partial<AllMetrics>>(config: DataPointConfig<T>[] | DataPointConfig<T>[][]): config is DataPointConfig<T>[][] =>
  config && config.length > 0 && Array.isArray(config[0])
const mapConfig = <T extends Partial<AllMetrics>>(config: TimelineConfiguration<T>, names: string[]): DataPointConfig<T>[] =>
  names.map((l) => config.dataConfig.find((d) => d.name === l)).filter(NonNullable)

function getConfig<T extends Partial<AllMetrics>, N extends string[] | string[][]>(config: TimelineConfiguration<T>, names: N): N extends string[][] ? DataPointConfig<T>[][] : DataPointConfig<T>[]
function getConfig<T extends Partial<AllMetrics>>(config: TimelineConfiguration<T>, names: string[] | string[][]): DataPointConfig<T>[] | DataPointConfig<T>[][] {
  return isMultiLevel(names) ? names.map((l) => mapConfig(config, l)) : mapConfig(config, names)
}

const isComputable =
  <T extends Partial<AllMetrics>>(args: BuildArgs<T>) =>
  (config: DataPointConfig<T>): boolean =>
    config.isComputable ? config.isComputable(args) : true

export const useMetrics = <T extends Partial<AllMetrics>, C extends MetricConfiguration<T>>({
  config,
  data,
  comparisonData,
  currencySymbol,
  onMetricChange,
}: {
  config: C[]
  data: T
  comparisonData?: T
  currencySymbol: CurrencySymbol
  onMetricChange: (metric: string) => void
}) => {
  const [current, setCurrent] = useState<number>(0)
  return useMemo(() => {
    const metrics = config.map<TimelineMetricProps>(({ metric, dataConfig }, index) => {
      const metricDataPoint = dataConfig.flat().find((d) => d.name === metric.name)
      if (!metricDataPoint) {
        throw Error(`missing metric data point configuration for ${metric.name}`)
      }
      const f = metricDataPoint.getFormula({ data, currencySymbol })
      const rawValue = f && f.isComputable(data) ? f.sum(data) : 0
      return {
        active: current === index,
        label: metricDataPoint.label ?? ANALYTICS_CHART_COLOR_CONFIG[metric.name]?.title ?? '',
        value: f ? f.displayable(rawValue, currencySymbol).toString() : undefined,
        notApplicable: !f,
        onClick: () => {
          onMetricChange(metric.name)
          f && setCurrent(index)
        },
        trackingLabel: `ana-chart-metric-${normalizeAsTrackingLabel(metric.name)}-apply`,
        tooltipText: metric.tooltipText,
      }
    })

    const currentConfig = config[current]
    const metricMessage = currentConfig.metric.message ? currentConfig.metric.message({ data, comparisonData, currencySymbol }) : undefined

    return {
      metrics,
      currentMetric: metrics[current],
      currentConfig,
      metricMessage,
    }
  }, [current, config, data, comparisonData, currencySymbol, onMetricChange])
}

export const useTimelineChart = <T extends Partial<AllMetrics> & { epoch: string[] }>({
  currentConfig,
  data,
  comparisonData,
  currencySymbol,
  currentEpoch,
  timezone,
  chartType,
  type = 'number',
}: {
  currentConfig: TimelineConfiguration<T>
  data: T
  comparisonData?: T
  currencySymbol: CurrencySymbol
  currentEpoch: DateTime
  timezone: Timezone
  chartType?: GoogleChartWrapperChartType
  type?: 'number' | 'percentage'
}) => {
  const legends = useLegends(currentConfig, { data, comparisonData, currencySymbol })
  const colors = useMemo(() => legends.map((l) => l.iconColor), [legends])
  const convertData = useGetChartTimelineData(currentConfig, timezone, type)

  const chartOptions = useChartOptions({
    currentConfig,
    colors,
    data,
    currencySymbol,
    comparisonData,
  })

  let chartData: unknown[] | undefined
  if (data.epoch.length === 0) {
    chartData = undefined
  } else {
    chartData = convertData(data, comparisonData, currentEpoch, currencySymbol)
  }
  const chart: ReactGoogleChartProps = { chartType: chartType ?? 'ComboChart', options: chartOptions, data: chartData }
  return {
    chart,
    legends,
  }
}

const useGetChartTimelineData = <T extends Partial<AllMetrics> & { epoch: string[] }>(currentConfig: TimelineConfiguration<T>, timezone: Timezone, type: 'number' | 'percentage' = 'number') => {
  return (data: T, comparisonData: T | undefined, currentEpoch: DateTime, currencySymbol: CurrencySymbol) => {
    const dataConfig = getConfig(currentConfig, currentConfig.legendConfig).filter(isComputable<T>({ data, comparisonData, currencySymbol }))
    const tooltipConfig = getConfig(currentConfig, currentConfig.tooltipConfig)
    const dateFormat = getChartTooltipDateFormat(data.epoch)
    const chartDataValues: (string | Date | number)[][] = data.epoch.map((epoch, index) => [
      convertChartDateTime(epoch, timezone),
      getChartTooltip({
        epoch: parseDateTime(epoch, timezone),
        tooltipConfig,
        data,
        comparisonData,
        index,
        currentEpoch,
        dateFormat,
        currencySymbol,
        type,
      }),
      ...dataConfig.map((d) => {
        const usedData = d.name === 'comparisonLine' ? (comparisonData ?? ({} as T)) : data
        const formula = d.getFormula({ data, comparisonData, currencySymbol })
        const isComputable =
          !!formula &&
          (d.isComputable ?? (() => true))({
            data,
            comparisonData,
            currencySymbol,
          }) &&
          formula.isComputable(usedData ?? {})
        return isComputable ? formula.compute(usedData, index) : 0
      }),
    ])

    return [[{ type: 'datetime' }, { type: 'string', role: 'tooltip', p: { html: true } }, ...dataConfig.map((l) => l.name)], ...chartDataValues]
  }
}

const useLegends = <T extends Partial<AllMetrics>>(currentConfig: TimelineConfiguration<T>, args: BuildArgs<T>) => {
  return useMemo(
    () =>
      getConfig(currentConfig, currentConfig.legendConfig)
        .filter(isComputable<T>(args))
        .map((l) => {
          const formula = l.getFormula(args)
          return {
            ...l,
            iconColor: l.iconColor ?? ANALYTICS_CHART_COLOR_CONFIG[l.name]?.color ?? Colors.Jet,
            label: l.label ?? ANALYTICS_CHART_COLOR_CONFIG[l.name]?.title ?? '',
            value: l.withValue && formula ? formula.displayable(formula.sum(args.data), args.currencySymbol) : undefined,
            tooltip: l.legendTooltip,
          }
        }),
    [currentConfig, args]
  )
}

const useChartOptions = <T extends Partial<AllMetrics>>({
  currentConfig,
  colors,
  data,
  comparisonData,
  currencySymbol,
}: {
  currentConfig: TimelineConfiguration<T>
  colors: string[]
  data: T
  comparisonData?: T
  currencySymbol: CurrencySymbol
}) => {
  return useMemo(() => {
    const { getChartOptions, legendConfig } = currentConfig
    return {
      ...AnalyticsDefaultChartOptions,
      colors,
      ...(getChartOptions
        ? getChartOptions({
            data,
            comparisonData,
            currencySymbol,
            legendsConfig: getConfig(currentConfig, legendConfig)
              .filter(isComputable<T>({ data, comparisonData, currencySymbol }))
              .map((a) => a.name),
          })
        : {}),
    }
  }, [currentConfig, colors, data, comparisonData, currencySymbol])
}

type TooltipArgs<T extends Partial<AllMetrics>> = {
  epoch: DateTime
  tooltipConfig: DataPointConfig<T>[] | DataPointConfig<T>[][]
  data: T
  comparisonData: T | undefined
  index: number
  currentEpoch: DateTime
  dateFormat: string
  currencySymbol: CurrencySymbol
  type?: 'number' | 'percentage'
}
const getChartTooltip = <T extends Partial<AllMetrics> & { epoch: string[] }>(args: TooltipArgs<T>) => {
  const { epoch, tooltipConfig, data, comparisonData, index, currentEpoch, currencySymbol, dateFormat, type = 'number' } = args
  if (epoch > currentEpoch) {
    return '<div style="display: none"></div>'
  }
  const normalizedConfig: DataPointConfig<T>[][] = isMultiLevelDataPointConfig(tooltipConfig) ? tooltipConfig : [tooltipConfig]
  const dataConfig = normalizedConfig.map((arr) => arr.filter(isComputable(args))).filter((arr) => arr.length)

  const metrics: ChartTooltipMetricProps[][] = dataConfig.map((group) => {
    const entries = group
      .map((c) => {
        const usedData = c.name === 'comparisonLine' ? (comparisonData ?? ({} as T)) : data
        const formula = c.getFormula({ data, comparisonData, currencySymbol })
        return formula ? { c, formula, computed: formula.compute(usedData, index) } : undefined
      })
      .filter(NonNullable)
    // In percentage mode, stacked series (group of 2+) are shown as a share of the group total;
    // single-member groups (e.g. the total row) stay absolute, like the server-side sigma row.
    const asShare = type === 'percentage' && entries.length > 1
    const groupTotal = entries.reduce((sum, { computed }) => sum + computed, 0)
    return entries.map(({ c, formula, computed }) => ({
      label: c.getTooltipLabel ? c.getTooltipLabel(args) : (c.label ?? ANALYTICS_CHART_COLOR_CONFIG[c.name]?.title),
      iconName: c.iconName ?? 'square',
      iconColor: c.iconColor ?? ANALYTICS_CHART_COLOR_CONFIG[c.name]?.color ?? Colors.Jet,
      value: asShare ? asPercentage(groupTotal ? (computed / groupTotal) * 100 : 0) : formula.displayable(computed, currencySymbol),
      secondary: c.name === 'comparisonLine',
    }))
  })
  return generateChartTooltip({ date: epoch.toFormat(dateFormat), metrics })
}
